/**
 * UAT hotfix (2026-07-25) · Business Unit master admin surface.
 * Enterprise org-level master (REEDOS correction #1). v1 write: super_admin only.
 */
import { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../../components/ui/table";
import {
    Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";


export default function BusinessUnitMasterPage() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [includeInactive, setIncludeInactive] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState({ code: "", name: "", description: "" });

    const load = () => {
        setLoading(true);
        api.get(`/org/business-unit/list?include_inactive=${includeInactive}&page_size=200`)
            .then((r) => setItems(r.data?.items || []))
            .catch((e) => toast.error(formatApiError(e)))
            .finally(() => setLoading(false));
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [includeInactive]);

    const startCreate = () => {
        setEditing(null);
        setForm({ code: "", name: "", description: "" });
        setDialogOpen(true);
    };
    const startEdit = (bu) => {
        setEditing(bu);
        setForm({ code: bu.code, name: bu.name, description: bu.description || "" });
        setDialogOpen(true);
    };
    const save = async () => {
        try {
            if (editing) {
                await api.patch(`/org/business-unit/${editing.id}`, {
                    name: form.name, description: form.description,
                });
                toast.success("Business Unit updated.");
            } else {
                await api.post("/org/business-unit", form);
                toast.success("Business Unit created.");
            }
            setDialogOpen(false);
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };
    const toggleActive = async (bu) => {
        try {
            await api.patch(`/org/business-unit/${bu.id}`, { is_active: !bu.is_active });
            toast.success(`Business Unit ${bu.is_active ? "deactivated" : "reactivated"}.`);
            load();
        } catch (e) {
            const d = e?.response?.data?.detail;
            if (typeof d === "object" && d?.reason_code === "bu_deactivate_blocked_by_active_cost_centres") {
                toast.error(d.message);
            } else { toast.error(formatApiError(e)); }
        }
    };

    return (
        <div className="p-6 space-y-4" data-testid="bu-master-page">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-xl font-semibold">Business Unit Master</h1>
                    <p className="text-sm text-slate-500">
                        Organisation-level Business Units. Used by internal projects and Cost Centres.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <label className="flex items-center gap-1 text-xs text-slate-500">
                        <input type="checkbox" checked={includeInactive}
                            onChange={(e) => setIncludeInactive(e.target.checked)}
                            data-testid="bu-include-inactive" />
                        Include inactive
                    </label>
                    <Button onClick={startCreate} data-testid="bu-create-btn">
                        + New Business Unit
                    </Button>
                </div>
            </div>
            <div className="border rounded-md">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Code</TableHead>
                            <TableHead>Name</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {loading && (
                            <TableRow><TableCell colSpan={4} className="text-center text-slate-400">Loading...</TableCell></TableRow>
                        )}
                        {!loading && items.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={4} className="text-center text-slate-400 py-6">
                                    No Business Units. Click "+ New Business Unit" to start.
                                </TableCell>
                            </TableRow>
                        )}
                        {items.map((bu) => (
                            <TableRow key={bu.id} data-testid={`bu-row-${bu.id}`}>
                                <TableCell className="font-mono text-xs">{bu.code}</TableCell>
                                <TableCell>{bu.name}</TableCell>
                                <TableCell>
                                    <Badge variant={bu.is_active ? "default" : "secondary"}>
                                        {bu.is_active ? "Active" : "Inactive"}
                                    </Badge>
                                </TableCell>
                                <TableCell className="text-right space-x-2">
                                    <Button size="sm" variant="outline" onClick={() => startEdit(bu)} data-testid={`bu-edit-${bu.id}`}>Edit</Button>
                                    <Button size="sm" variant="outline" onClick={() => toggleActive(bu)} data-testid={`bu-toggle-${bu.id}`}>
                                        {bu.is_active ? "Deactivate" : "Reactivate"}
                                    </Button>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </div>

            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent data-testid="bu-form-dialog">
                    <DialogHeader>
                        <DialogTitle>{editing ? "Edit Business Unit" : "New Business Unit"}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3 py-2">
                        <div>
                            <Label>Code <span className="text-rose-600">*</span></Label>
                            <Input value={form.code}
                                onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                                disabled={!!editing}
                                placeholder="e.g. OPS" data-testid="bu-form-code" />
                        </div>
                        <div>
                            <Label>Name <span className="text-rose-600">*</span></Label>
                            <Input value={form.name}
                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                                placeholder="e.g. Operations" data-testid="bu-form-name" />
                        </div>
                        <div>
                            <Label>Description</Label>
                            <Input value={form.description}
                                onChange={(e) => setForm({ ...form, description: e.target.value })}
                                data-testid="bu-form-description" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                        <Button onClick={save} data-testid="bu-form-submit">
                            {editing ? "Save changes" : "Create"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
